import type { MessagesToday } from './messages-today-types'
import type { MessageHome } from './message-homes'
import { el, money } from './ui'

export const unknownMessagesToday = (): MessagesToday => ({ day: null, sent: null, target: null, left: null, homes: null, bookedWeekPence: null, bookedTodayPence: null, bookedDays: null, bookedDayVisits: null, streak: null, handledToday: null })

// A failed independent read keeps its last known half. A new business-local day starts afresh.
export function mergeMessagesToday(previous: MessagesToday, next: MessagesToday): MessagesToday {
  const merged = { ...(next.day && next.day !== previous.day ? unknownMessagesToday() : previous) }
  const groups: (keyof MessagesToday)[][] = [['day'], ['sent', 'target', 'left'], ['homes'], ['bookedWeekPence', 'bookedTodayPence', 'bookedDays', 'bookedDayVisits'], ['streak'], ['handledToday']]
  for (const group of groups) {
    const known = group.every(key => next[key] !== null || key === 'bookedDayVisits') && (!group.includes('bookedDays') || !next.bookedDays?.some(n => n === null))
    // Keep coherent halves; a new sent count must not pair with an old target/left and earn a false completion.
    if (known) for (const key of group) Object.assign(merged, { [key]: next[key] })
  }
  return merged
}

export function ringProgress(data: MessagesToday): number | null {
  if (data.sent === null || data.target === null || data.left === null) return null
  if (data.target === 0) return 0
  return Math.min(1, Math.max(0, (data.target - data.left) / data.target))
}

export function weekBars(data: MessagesToday): { height: number; today: boolean }[] | null {
  if (!data.day || !data.bookedDays || data.bookedDays.length !== 7 || data.bookedDays.some(n => n === null)) return null
  const values = data.bookedDays as number[]
  const biggest = Math.max(0, ...values)
  const today = (new Date(`${data.day}T12:00:00Z`).getUTCDay() + 6) % 7
  return values.map((n, i) => ({ height: biggest ? Math.max(0, n / biggest) * 100 : 0, today: i === today }))
}

// The shared one-mark path, also used by Messages' clearedMark. Still unless this action just completed the day.
function tick(className = ''): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 64 64'); svg.setAttribute('class', `messages-progress-tick ${className}`)
  svg.setAttribute('aria-hidden', 'true')
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('d', 'M20 33l8 8 16-17'); svg.append(path)
  return svg
}

export class DailyCompletion {
  private previous: MessagesToday | null = null
  private seen = new Set<string>()
  private pending: { key: string; until: number } | null = null
  opened(): void { this.previous = null; this.pending = null }
  celebrating(owner: string, data: MessagesToday, now = Date.now()): boolean {
    return data.target! > 0 && data.left === 0 && this.pending?.key === `${owner}:${data.day}` && now < this.pending.until
  }
  played(owner: string, data: MessagesToday): void {
    if (this.pending?.key === `${owner}:${data.day}`) this.pending = null
  }
  update(owner: string, data: MessagesToday, visible: boolean, storage?: Pick<Storage, 'getItem' | 'setItem'>, now = Date.now()): boolean {
    if (ringProgress(data) === null || !data.day) return false
    const complete = data.target! > 0 && data.left === 0
    const crossed = this.previous?.day === data.day && this.previous.left !== null && this.previous.left > 0
    this.previous = data
    if (!complete) { this.pending = null; return false }
    const key = `roundly:messages-done:${owner}:${data.day}`
    let seen = this.seen.has(key)
    try { seen ||= !!storage?.getItem(key); storage?.setItem(key, '1') } catch { /* memory covers refused storage */ }
    this.seen.add(key)
    // Opening a completed day, returning from another screen and hidden arrivals never replay the mark.
    const celebrate = !!crossed && !seen && visible
    if (celebrate) this.pending = { key: `${owner}:${data.day}`, until: now + 600 }
    return celebrate
  }
}

export function paintMessagesBanner(banner: HTMLElement, data: MessagesToday, celebrate = false, onDay?: (index: number) => void): void {
  banner.hidden = false
  const progress = ringProgress(data)
  const left = el('div', { class: 'messages-daily-sent' })
  if (progress === null) left.append(el('span', { class: 'messages-daily-unknown', role: 'img', 'aria-label': 'Messages today not available' }, '…'))
  else {
    const complete = data.target! > 0 && data.left === 0
    const done = data.target! - data.left!
    const ring = el('span', { class: 'messages-daily-ring', role: 'img', 'aria-label': data.target === 0 ? `${data.sent} sent today, nothing due` : `${done} of ${data.target} sent today` })
    ring.style.background = `conic-gradient(var(--brand-accent) ${progress * 100}%, var(--messages-banner-track) 0)`
    const inside = el('span', { class: 'messages-daily-ring-inside' })
    inside.append(complete ? tick(celebrate ? 'messages-daily-complete' : '') : el('b', {}, data.target === 0 ? String(data.sent) : `${done}/${data.target}`), el('span', {}, 'sent'))
    ring.append(inside)
    left.append(ring, el('span', { class: 'messages-daily-left' }, data.target === 0 ? 'Nothing due today' : `${data.left} left today`))
    // The ring counts today's due people messaged, in Liam's word "sent" (1 Nov 2026: "4/10 sent"); a message to someone
    // not due today does not fill it.
  }
  const booked = el('div', { class: 'messages-daily-booked' }, el('span', { class: 'messages-daily-label' }, 'Booked this week'))
  const bars = weekBars(data)
  if (data.bookedWeekPence === null || data.bookedTodayPence === null || bars === null) booked.append(el('b', { class: 'messages-daily-unknown', role: 'img', 'aria-label': 'Bookings not available' }, '…'))
  else {
    booked.append(el('b', { class: 'messages-daily-money' }, money(data.bookedWeekPence)))
    if (data.bookedTodayPence > 0) booked.append(el('span', { class: 'messages-daily-gain' }, `+${money(data.bookedTodayPence)} today`))
    const chart = el('div', { class: 'messages-daily-bars', role: 'img', 'aria-label': ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => `${day}: ${money(data.bookedDays![i]!)}`).join(', ') })
    for (const bar of bars) {
      const node = el('span', { class: `${bar.today ? 'is-today' : ''}${bar.height === 0 ? ' is-empty' : ''}`.trim(), 'aria-hidden': 'true' })
      node.style.height = `${bar.height}%`; chart.append(node)
    }
    booked.append(chart)
    // Tap a day's bar to see who you booked that day (Liam, 4 Nov 2026, 3A): one button over each day that has bookings.
    if (onDay && data.bookedDayVisits?.length === 7) {
      const names = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
      const hits = el('div', { class: 'messages-daily-hits' })
      data.bookedDayVisits.forEach((list, i) => {
        const b = el('button', { type: 'button', class: 'messages-daily-hit', 'aria-label': `Booked on ${names[i]}: ${list.length} ${list.length === 1 ? 'visit' : 'visits'}, ${money(data.bookedDays![i]!)}` })
        if (!list.length) b.disabled = true
        b.addEventListener('click', () => onDay(i))
        hits.append(b)
      })
      booked.append(hits)
    }
  }
  banner.replaceChildren(left, el('span', { class: 'messages-daily-divider', 'aria-hidden': 'true' }), booked)
}

export function paintTileProgress(slot: HTMLElement, data: MessagesToday, home: MessageHome): void {
  const work = data.homes?.[home]
  slot.hidden = !work || work.total === 0
  slot.replaceChildren()
  if (slot.hidden || !work) return
  slot.setAttribute('role', 'img')
  slot.setAttribute('aria-label', `${work.done} of ${work.total} handled today`)
  if (work.done >= work.total) slot.append(tick())
  else {
    const fill = el('span', { class: 'messages-tile-progress-fill' })
    fill.style.width = `${Math.min(100, Math.max(0, work.done / work.total * 100))}%`
    slot.append(el('span', { class: 'messages-tile-progress-track', 'aria-hidden': 'true' }, fill))
  }
}

export function streakNote(streak: number | null): string {
  return streak !== null && streak >= 2 ? `${streak}-day streak` : ''
}

export function paintEnquiryStreak(slot: HTMLElement, data: MessagesToday): void {
  slot.hidden = !streakNote(data.streak)
  slot.replaceChildren()
  if (!slot.hidden) slot.append(el('b', { class: 'messages-streak-number' }, String(data.streak)), el('span', {}, 'day streak'), el('span', { class: 'messages-streak-handled' }, `· ${data.handledToday ?? '…'} handled today`))
}
